 import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fuarAlaniService } from '../services/fuarAlaniService';
import i18n from "../i18n";

// ---- Fuar Alanı Thunks ----
export const fetchFuarAlaniList = createAsyncThunk('fuarAlani/fetchList', async (_, { rejectWithValue }) => {
  try { return await fuarAlaniService.getFuarAlaniList(); }
  catch (err) { return rejectWithValue(err.response?.data || err.message); }
});

export const fetchFuarAlaniDetail = createAsyncThunk('fuarAlani/fetchDetail', async (id, { rejectWithValue }) => {
  try { return await fuarAlaniService.getFuarAlaniDetail(id); }
  catch (err) { return rejectWithValue(err.response?.data || err.message); }
});

export const createFuarAlani = createAsyncThunk('fuarAlani/create', async (payload, { rejectWithValue }) => {
  try { return await fuarAlaniService.createFuarAlani(payload); }
  catch (err) { return rejectWithValue(err.response?.data || err.message); }
});

export const updateFuarAlani = createAsyncThunk('fuarAlani/update', async ({ id, payload }, { rejectWithValue }) => {
  try { return await fuarAlaniService.updateFuarAlani(id, payload); }
  catch (err) { return rejectWithValue(err.response?.data || err.message); }
});

export const deleteFuarAlani = createAsyncThunk('fuarAlani/delete', async (id, { rejectWithValue }) => {
  try { await fuarAlaniService.deleteFuarAlani(id); return id; }
  catch (err) { return rejectWithValue(err.response?.data || err.message); }
});

// ---- Salon Thunks ----
export const fetchSalonList = createAsyncThunk('fuarAlani/fetchSalons', async (fuarAlaniId, { rejectWithValue }) => {
  try { const salons = await fuarAlaniService.getSalonList(fuarAlaniId); return { fuarAlaniId, salons }; }
  catch (err) { return rejectWithValue(err.response?.data || err.message); }
});

export const createSalon = createAsyncThunk('fuarAlani/createSalon', async ({ fuarAlaniId, payload }, { rejectWithValue }) => {
  try { const salon = await fuarAlaniService.createSalon(fuarAlaniId, payload); return { fuarAlaniId, salon }; }
  catch (err) { return rejectWithValue(err.response?.data || err.message); }
});

export const updateSalon = createAsyncThunk('fuarAlani/updateSalon', async ({ salonId, fuarAlaniId, payload }, { rejectWithValue }) => {
  try { const salon = await fuarAlaniService.updateSalon(salonId, payload); return { fuarAlaniId, salon }; }
  catch (err) { return rejectWithValue(err.response?.data || err.message); }
});

export const deleteSalon = createAsyncThunk('fuarAlani/deleteSalon', async ({ salonId, fuarAlaniId }, { rejectWithValue }) => {
  try { await fuarAlaniService.deleteSalon(salonId); return { salonId, fuarAlaniId }; }
  catch (err) { return rejectWithValue(err.response?.data || err.message); }
});

// ---- Slice ----
const fuarAlaniSlice = createSlice({
  name: 'fuarAlani',
  initialState: {
    list: [],
    selectedAlani: null,
    salons: [],
    loading: false,
    salonLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => { state.error = null; },
    clearSuccess: (state) => { state.success = null; },
    clearSelectedAlani: (state) => { state.selectedAlani = null; state.salons = []; },
  },
  extraReducers: (builder) => {
    const pending = (key) => (state) => { state[key] = true; state.error = null; };
    const rejected = (key) => (state, action) => { state[key] = false; state.error = action.payload; };

    builder
      .addCase(fetchFuarAlaniList.pending, pending('loading'))
      .addCase(fetchFuarAlaniList.fulfilled, (state, action) => { state.loading = false; state.list = action.payload; })
      .addCase(fetchFuarAlaniList.rejected, rejected('loading'))

      .addCase(fetchFuarAlaniDetail.pending, pending('loading'))
      .addCase(fetchFuarAlaniDetail.fulfilled, (state, action) => { state.loading = false; state.selectedAlani = action.payload; state.salons = action.payload.salons || []; })
      .addCase(fetchFuarAlaniDetail.rejected, rejected('loading'))

      .addCase(createFuarAlani.pending, pending('loading'))
      .addCase(createFuarAlani.fulfilled, (state, action) => { state.loading = false; state.success = i18n.t("services:fuarAlaniSlice.fuarAlaniOlusturuldu"); state.list.push(action.payload); })
      .addCase(createFuarAlani.rejected, rejected('loading'))

      .addCase(updateFuarAlani.pending, pending('loading'))
      .addCase(updateFuarAlani.fulfilled, (state, action) => {
        state.loading = false; state.success = i18n.t("services:fuarAlaniSlice.fuarAlaniGuncellendi");
        const idx = state.list.findIndex((a) => a.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.selectedAlani?.id === action.payload.id) state.selectedAlani = action.payload;
      })
      .addCase(updateFuarAlani.rejected, rejected('loading'))

      .addCase(deleteFuarAlani.pending, pending('loading'))
      .addCase(deleteFuarAlani.fulfilled, (state, action) => {
        state.loading = false; state.success = i18n.t("services:fuarAlaniSlice.fuarAlaniSilindi");
        const idx = state.list.findIndex((a) => a.id === action.payload);
        if (idx !== -1) state.list[idx].isActive = false;
      })
      .addCase(deleteFuarAlani.rejected, rejected('loading'))

      .addCase(fetchSalonList.pending, pending('salonLoading'))
      .addCase(fetchSalonList.fulfilled, (state, action) => { state.salonLoading = false; state.salons = action.payload.salons; })
      .addCase(fetchSalonList.rejected, rejected('salonLoading'))

      .addCase(createSalon.pending, pending('salonLoading'))
      .addCase(createSalon.fulfilled, (state, action) => {
        state.salonLoading = false; state.success = 'Salon eklendi';
        state.salons.push(action.payload.salon);
        const aIdx = state.list.findIndex((a) => a.id === action.payload.fuarAlaniId);
        if (aIdx !== -1) { if (!state.list[aIdx].salons) state.list[aIdx].salons = []; state.list[aIdx].salons.push(action.payload.salon); }
      })
      .addCase(createSalon.rejected, rejected('salonLoading'))

      .addCase(updateSalon.pending, pending('salonLoading'))
      .addCase(updateSalon.fulfilled, (state, action) => {
        state.salonLoading = false; state.success = i18n.t("services:fuarAlaniSlice.salonGuncellendi");
        const idx = state.salons.findIndex((s) => s.id === action.payload.salon.id);
        if (idx !== -1) state.salons[idx] = action.payload.salon;
      })
      .addCase(updateSalon.rejected, rejected('salonLoading'))

      .addCase(deleteSalon.pending, pending('salonLoading'))
      .addCase(deleteSalon.fulfilled, (state, action) => {
        state.salonLoading = false; state.success = 'Salon silindi';
        state.salons = state.salons.filter((s) => s.id !== action.payload.salonId);
        const aIdx = state.list.findIndex((a) => a.id === action.payload.fuarAlaniId);
        if (aIdx !== -1 && state.list[aIdx].salons)
          state.list[aIdx].salons = state.list[aIdx].salons.filter((s) => s.id !== action.payload.salonId);
      })
      .addCase(deleteSalon.rejected, rejected('salonLoading'));
  },
});

export const { clearError, clearSuccess, clearSelectedAlani } = fuarAlaniSlice.actions;
export default fuarAlaniSlice.reducer;

